import React, { FC, ReactElement, useEffect, useState } from "react";
import {
  Tabs, Table, Modal, Input, message, Popconfirm, Upload, Image, Progress,
} from "antd";
import { PlusOutlined, DeleteOutlined, UploadOutlined, HomeOutlined, PictureOutlined, TagsOutlined, StarOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../../../constants/urlConstants";
import "./ManageHome.css";
import { toast } from "../../../utils/toast";

const { TabPane } = Tabs;

interface Slider         { id?: number; name: string; url: string; position?: number; perfumeId?: number; }
interface Brand          { id?: number; name: string; image: string; position?: number; }
interface PerfumeHighlight { id?: number; perfumeId: number; position?: number; }

const ManageHome: FC = (): ReactElement => {
  const [activeTab, setActiveTab]           = useState("sliders");
  const [sliders, setSliders]               = useState<Slider[]>([]);
  const [brands, setBrands]                 = useState<Brand[]>([]);
  const [highlights, setHighlights]         = useState<PerfumeHighlight[]>([]);
  const [loading, setLoading]               = useState(false);
  const [modalVisible, setModalVisible]     = useState(false);
  const [formData, setFormData]             = useState<any>({});
  const [selectedFile, setSelectedFile]     = useState<File | null>(null);
  const [preview, setPreview]               = useState<string | null>(null);
  const [fileList, setFileList]             = useState<any[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [slidersRes, brandsRes, featuredRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/v1/home/sliders`),
        axios.get(`${BASE_URL}/api/v1/home/brands`),
        axios.get(`${BASE_URL}/api/v1/home/featured`),
      ]);
      setSliders(slidersRes.data);
      setBrands(brandsRes.data);
      setHighlights(featuredRes.data);
    } catch (error) {
      toast("error","Failed to fetch home data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = ({ fileList: newFileList }: any) => {
    setFileList(newFileList);
    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj as File;
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null); setPreview(null);
    }
  };

  const openModal = () => {
    setModalVisible(true); setFormData({});
    setSelectedFile(null); setPreview(null);
    setFileList([]); setUploadProgress(0);
  };

  const closeModal = () => {
    setModalVisible(false);
    setSelectedFile(null); setPreview(null); setFileList([]);
  };

  const handleAdd = async () => {
    if (!formData.name && activeTab !== "featured") { message.warning("Please enter a name"); return; }
    const form = new FormData();
    try {
      setUploadProgress(0);
      if (activeTab === "sliders") {
        form.append("slider", new Blob([JSON.stringify({ name: formData.name, position: formData.position, perfumeId: formData.perfumeId })], { type: "application/json" }));
        if (!selectedFile) { message.warning("Please select an image"); return; }
        form.append("file", selectedFile, selectedFile.name);
        await axios.post(`${BASE_URL}/api/v1/home/sliders`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total)); },
        });
      } else if (activeTab === "brands") {
        form.append("brand", new Blob([JSON.stringify({ name: formData.name, position: formData.position })], { type: "application/json" }));
        if (!selectedFile) { message.warning("Please select an image"); return; }
        form.append("file", selectedFile, selectedFile.name);
        await axios.post(`${BASE_URL}/api/v1/home/brands`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total)); },
        });
      } else {
        await axios.post(`${BASE_URL}/api/v1/home/featured`, formData);
      }
    //  message.success("Added successfully!");
      toast("success", "Added successfully!");
      closeModal(); setFormData({}); setUploadProgress(0); fetchAll();
    } catch (error) {
       toast("error","Failed to add item");
       setUploadProgress(0);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const endpoint = activeTab === "sliders" ? "sliders" : activeTab === "brands" ? "brands" : "featured";
      await axios.delete(`${BASE_URL}/api/v1/home/${endpoint}/${id}`);
      toast("success", "Deleted successfully!");
     // message.success("Deleted successfully!");
      fetchAll();
    } catch { toast("error","Failed to delete item"); }
  };

  const deleteBtn = (id: number, label: string) => (
    <Popconfirm title={`Delete this ${label}?`} onConfirm={() => handleDelete(id)}
      okButtonProps={{ danger: true }} okText="Delete" cancelText="Cancel">
      <button className="mh-del-btn"><DeleteOutlined /></button>
    </Popconfirm>
  );

  const renderTable = () => {
    if (activeTab === "sliders") return (
      <Table dataSource={sliders} rowKey="id" loading={loading} scroll={{ x: true }}
        className="mh-table" rowClassName={() => "mh-table-row"}
        pagination={{ pageSize: 8, showSizeChanger: false }}
        columns={[
          { title: "ID",   dataIndex: "id",   width: 70 },
          { title: "Name", dataIndex: "name"             },
          { title: "Pos",  dataIndex: "position", width: 60 },
          { title: "Image", width: 140, render: (_, r) => (
            <Image src={r.url} alt={r.name} width={110} height={50}
              style={{ objectFit: "cover", borderRadius: 2 }} />
          )},
          { title: "Action", width: 80, render: (_, r) => deleteBtn(r.id!, "slider") },
        ]}
      />
    );

    if (activeTab === "brands") return (
      <Table dataSource={brands} rowKey="id" loading={loading} scroll={{ x: true }}
        className="mh-table" rowClassName={() => "mh-table-row"}
        pagination={{ pageSize: 8, showSizeChanger: false }}
        columns={[
          { title: "ID",   dataIndex: "id",   width: 70 },
          { title: "Name", dataIndex: "name"             },
          { title: "Pos",  dataIndex: "position", width: 60 },
          { title: "Image", width: 140, render: (_, r) => (
            <Image src={r.image} alt={r.name} width={110} height={50}
              style={{ objectFit: "contain", borderRadius: 2 }} />
          )},
          { title: "Action", width: 80, render: (_, r) => deleteBtn(r.id!, "brand") },
        ]}
      />
    );

    return (
      <Table dataSource={highlights} rowKey="id" loading={loading} scroll={{ x: true }}
        className="mh-table" rowClassName={() => "mh-table-row"}
        pagination={{ pageSize: 8, showSizeChanger: false }}
        columns={[
          { title: "ID",         dataIndex: "id",        width: 70 },
          { title: "Product ID", dataIndex: "perfumeId"             },
          { title: "Position",   dataIndex: "position",  width: 90 },
          { title: "Action",     width: 80, render: (_, r) => deleteBtn(r.id!, "highlight") },
        ]}
      />
    );
  };

  const tabCount = (tab: string) => {
    if (tab === "sliders")  return sliders.length;
    if (tab === "brands")   return brands.length;
    return highlights.length;
  };

  const modalTitle = activeTab === "sliders" ? "Slider" : activeTab === "brands" ? "Brand" : "Featured Product";

  return (
    <div className="mh-page">

      {/* ── Top bar ── */}
      <div className="mh-topbar">
        <div className="mh-topbar-left">
          <HomeOutlined className="mh-topbar-icon" />
          <span className="mh-topbar-title">Manage Home Page</span>
        </div>
        <button className="mh-add-btn" onClick={openModal}>
          <PlusOutlined /> Add New
        </button>
      </div>

      {/* ── Breadcrumb ── */}
      <div className="mh-breadcrumb">Dashboard &rsaquo; Manage Home Page</div>

      {/* ── Content card ── */}
      <div className="mh-card-wrap">
        <div className="mh-card">

          {/* ── Tabs ── */}
          <div className="mh-tabs">
            {[
              { key: "sliders",  label: "Sliders",          icon: <PictureOutlined /> },
              { key: "brands",   label: "Brands",            icon: <TagsOutlined />   },
              { key: "featured", label: "Featured Products", icon: <StarOutlined />   },
            ].map((t) => (
              <button
                key={t.key}
                className={`mh-tab${activeTab === t.key ? " active" : ""}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.icon} {t.label}
                <span className="mh-tab-count">{tabCount(t.key)}</span>
              </button>
            ))}
          </div>

          {/* ── Table ── */}
          <div className="mh-table-wrap">
            {renderTable()}
          </div>
        </div>
      </div>

      {/* ── Add Modal ── */}
      <Modal
        title={null}
        open={modalVisible}
        onOk={handleAdd}
        onCancel={closeModal}
        okText="Save"
        cancelText="Cancel"
        okButtonProps={{ className: "mh-modal-ok-btn" }}
        className="mh-modal"
      >
        <div className="mh-modal-header">
          <PlusOutlined />
          <span>Add New {modalTitle}</span>
        </div>

        <div className="mh-modal-body">

          {/* SLIDER */}
          {activeTab === "sliders" && (
            <>
              <div className="mh-field-hint">Recommended image size: 1600 × 300 px</div>
              <Input placeholder="Product ID (optional)" type="number" size="large"
                value={formData.perfumeId}
                onChange={(e) => setFormData({ ...formData, perfumeId: Number(e.target.value) })}
                className="mh-input" />
              <Input placeholder="Slider Name *" size="large"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mh-input" />
              <Input placeholder="Position" type="number" size="large"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: Number(e.target.value) })}
                className="mh-input" />
              <Upload beforeUpload={() => false} fileList={fileList} onChange={handleUpload}
                showUploadList={{ showPreviewIcon: false, showRemoveIcon: true }}
                onRemove={() => { setSelectedFile(null); setPreview(null); setFileList([]); }}>
                <button type="button" className="mh-upload-btn"><UploadOutlined /> Upload Slider Image</button>
              </Upload>
              {preview && <Image src={preview} alt="preview" width={200} className="mh-preview" />}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <Progress percent={uploadProgress} status="active" style={{ marginTop: 10 }} />
              )}
            </>
          )}

          {/* BRAND */}
          {activeTab === "brands" && (
            <>
              <Input placeholder="Brand Name *" size="large"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mh-input" />
              <Input placeholder="Position (optional)" type="number" size="large"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: Number(e.target.value) })}
                className="mh-input" />
              <Upload beforeUpload={() => false} fileList={fileList} onChange={handleUpload}
                showUploadList={{ showPreviewIcon: false, showRemoveIcon: true }}
                onRemove={() => { setSelectedFile(null); setPreview(null); setFileList([]); }}>
                <button type="button" className="mh-upload-btn"><UploadOutlined /> Upload Brand Image</button>
              </Upload>
              {preview && <Image src={preview} alt="preview" width={200} className="mh-preview" />}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <Progress percent={uploadProgress} status="active" style={{ marginTop: 10 }} />
              )}
            </>
          )}

          {/* FEATURED */}
          {activeTab === "featured" && (
            <>
              <div className="mh-field-hint">Enter the Product ID to feature it on the home page.</div>
              <Input placeholder="Product ID *" type="number" size="large"
                value={formData.perfumeId}
                onChange={(e) => setFormData({ ...formData, perfumeId: Number(e.target.value) })}
                className="mh-input" />
              <Input placeholder="Position (optional)" type="number" size="large"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: Number(e.target.value) })}
                className="mh-input" />
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ManageHome;